/**
 * Entra application roles and what each one may reach.
 *
 * Roles are defined on the app registration and assigned per user under
 * Enterprise applications. Entra puts them in the `roles` claim of both the ID
 * token and the access token, so the UI and the API reach the same conclusion
 * without an extra round trip.
 *
 * This module is presentation only. The API enforces the same rules in
 * backend/api/authz.py, and that is the copy that matters: a hidden menu item
 * is not a permission, because anyone can type the URL and the browser holds a
 * usable token either way. The two lists are kept deliberately parallel, so if
 * you add a route here, add its endpoints there.
 */
import { useMemo } from "react";
import { useAccount, useMsal } from "@azure/msal-react";

export const ROLE_ADMIN = "Admin" as const;
export const ROLE_PBI_MANAGER = "PowerBI.Manager" as const;
export const ROLE_PBI_MON = "PowerBI.Mon" as const;

export type AppRole =
  | typeof ROLE_ADMIN
  | typeof ROLE_PBI_MANAGER
  | typeof ROLE_PBI_MON;

const KNOWN_ROLES: readonly string[] = [
  ROLE_ADMIN,
  ROLE_PBI_MANAGER,
  ROLE_PBI_MON,
];

/**
 * Route patterns a PowerBI.Manager may open, exactly as written in App.tsx.
 *
 * Everything else is Admin-only. Listing what is allowed rather than what is
 * denied means a page added next month is closed to this role until someone
 * opens it on purpose.
 */
export const MANAGER_ROUTES: readonly string[] = [
  "/workspaces",
  "/workspaces/:workspaceId",
  "/workspaces/:workspaceId/reports",
  "/workspaces/:workspaceId/artifacts/:artifactType/:artifactId",
  "/reports",
  "/reports/:reportId",
  "/gateways",
  "/dataflows",
  "/info",
  "/settings",
];

/**
 * Route patterns a PowerBI.Mon may open — the monitored workspaces, the
 * reports inside them, and what those reports are made of.
 *
 * A strict subset of MANAGER_ROUTES. Gateways, dataflows and the status page
 * are tenant-wide views with no workspace to narrow them by, so this role does
 * not get them at all rather than getting them whole.
 *
 * Unlike the other two roles, hiding pages is not the whole restriction: the
 * API also limits *which rows* these pages return, in backend/api/scope.py.
 * Nothing here should be read as a data boundary.
 */
export const MON_ROUTES: readonly string[] = [
  "/workspaces",
  "/workspaces/:workspaceId",
  "/workspaces/:workspaceId/reports",
  "/workspaces/:workspaceId/artifacts/:artifactType/:artifactId",
  "/reports",
  "/reports/:reportId",
  "/settings",
];

/** Where a role lands on sign-in, and where an out-of-bounds URL sends it. */
export const NO_ACCESS_ROUTE = "/no-access";

export function rolesFromClaims(claims: unknown): AppRole[] {
  const raw = (claims as { roles?: unknown } | null | undefined)?.roles;
  const list = typeof raw === "string" ? [raw] : Array.isArray(raw) ? raw : [];
  // An unrecognised role is dropped rather than trusted: renaming a role in
  // the portal without updating this file must revoke access, not grant it.
  return list.filter((r): r is AppRole => typeof r === "string" && KNOWN_ROLES.includes(r));
}

// Widest role first, so an account holding two roles gets the union of what
// they reach rather than whichever happens to be checked first.
export function canReach(route: string, roles: readonly AppRole[]): boolean {
  if (roles.includes(ROLE_ADMIN)) return true;
  if (roles.includes(ROLE_PBI_MANAGER)) return MANAGER_ROUTES.includes(route);
  if (roles.includes(ROLE_PBI_MON)) return MON_ROUTES.includes(route);
  return false;
}

export function homePathFor(roles: readonly AppRole[]): string {
  if (roles.includes(ROLE_ADMIN)) return "/";
  if (roles.includes(ROLE_PBI_MANAGER)) return "/workspaces";
  if (roles.includes(ROLE_PBI_MON)) return "/workspaces";
  return NO_ACCESS_ROUTE;
}

export interface RoleState {
  roles: AppRole[];
  isAdmin: boolean;
  isManager: boolean;
  /**
   * PowerBI.Mon and nothing wider. False when the account also holds Admin or
   * PowerBI.Manager, because then none of this role's limits apply — a
   * narrower role must not take controls away from a wider one held with it.
   */
  isMon: boolean;
  /** False when the account holds no known role — nothing is reachable. */
  hasAnyRole: boolean;
  canReach: (route: string) => boolean;
  /** Landing page for this role; also the target for out-of-bounds URLs. */
  homePath: string;
  signedInAs: string;
}

export function useRoles(): RoleState {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || null);
  const claims = account?.idTokenClaims;

  return useMemo(() => {
    const roles = rolesFromClaims(claims);
    return {
      roles,
      isAdmin: roles.includes(ROLE_ADMIN),
      isManager: roles.includes(ROLE_PBI_MANAGER),
      isMon:
        roles.includes(ROLE_PBI_MON) &&
        !roles.includes(ROLE_ADMIN) &&
        !roles.includes(ROLE_PBI_MANAGER),
      hasAnyRole: roles.length > 0,
      canReach: (route: string) => canReach(route, roles),
      homePath: homePathFor(roles),
      signedInAs: account?.username ?? "",
    };
  }, [claims, account?.username]);
}
